<template>
  <vonap-promised :promise="promise">
    <vonap-fullscreen-dialog
      :title="currentClient.name || 'New Client'"
      :show-close-button="!isProfilePage"
      :show-save-button="!isDisabled"
      @save="formSubmit"
      @close="show = false"
    >
      <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="formSubmit">
        <v-tabs v-model="selectedTab">
          <v-tab>General</v-tab>
          <v-tab v-for="market in marketsInOrder" :key="market.marketId"
            >{{ market.marketCode }}
          </v-tab>
          <v-tab-item>
            <v-container fluid grid-list-sm>
              <v-layout row wrap>
                <v-flex lg3 md3 xs12>
                  <v-layout row wrap>
                    <v-flex xs12>
                      <v-text-field
                        label="Name"
                        v-model="currentClient.name"
                        required
                        :rules="[v => !!v || 'Client name is required']"
                        :disabled="isDisabled"
                      />
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex xs12>
                      <v-select
                        label="Fiscal Year End"
                        v-model="currentClient.fiscalYearEnd"
                        item-value="month"
                        item-text="name"
                        :items="months"
                        :disabled="isDisabled"
                        required
                      />
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex xs12>
                      <v-text-field
                        type="number"
                        label="Historical Months"
                        v-model="currentClient.historicalMonthsNb"
                        :rules="[v => !!v || 'Historical months is required']"
                        :disabled="isDisabled"
                        required
                      />
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex xs12>
                      <v-select
                        label="Timezone"
                        v-model="currentClient.timeZoneId"
                        item-value="value"
                        item-text="name"
                        :items="timezones"
                        :disabled="isDisabled"
                        required
                      />
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap>
                    <v-flex xs12>
                      <v-text-field
                        label="Phone"
                        v-model="currentClient.phoneNumber"
                        :disabled="isDisabled"
                      />
                    </v-flex>
                  </v-layout>
                  <v-layout row wrap>
                    <v-flex xs12>
                      <v-text-field
                        label="Email"
                        v-model="currentClient.email"
                        :disabled="isDisabled"
                      />
                    </v-flex>
                    <v-flex xs12>
                      <v-select
                        :items="usersClientData"
                        v-model="currentClient.analystEmail"
                        label="Analyst"
                        item-text="email"
                        item-value="email"
                        :disabled="isDisabled"
                      ></v-select>
                    </v-flex>
                    <v-flex xs12>
                      <v-select
                        :items="usersClientData"
                        v-model="currentClient.responsibleEmail"
                        label="Responsible"
                        item-text="email"
                        item-value="email"
                        :disabled="isDisabled"
                      ></v-select>
                    </v-flex>
                    <v-flex xs12>
                      <v-select
                        :items="usersClientData"
                        v-model="currentClient.managerEmail"
                        label="Manager"
                        item-text="email"
                        item-value="email"
                        :disabled="isDisabled"
                      ></v-select>
                    </v-flex>
                  </v-layout>
                </v-flex>

                <v-flex lg8 offset-lg1 md8 offset-md1 xs12>
                  <v-layout row wrap>
                    <v-flex xs12 md5 lg4>
                      <v-layout row wrap>
                        <v-flex xs6 md4 lg4>
                          <vonap-form-label label="Active">
                            <v-switch
                              v-model="currentClient.isActive"
                              :disabled="isDisabled && canSetActive"
                            ></v-switch>
                          </vonap-form-label>
                        </v-flex>
                        <v-flex xs6 md6 lg6>
                          <vonap-form-label label="Send Alerts to Client">
                            <v-switch
                              v-model="currentClient.sendAlertToClient"
                              :disabled="isDisabled"
                            ></v-switch>
                          </vonap-form-label>
                        </v-flex>
                        <v-flex xs6 md6 lg6>
                          <vonap-form-label label="MTM Position Reconciled">
                            <v-switch
                              v-model="currentClient.mtmPositionReconciled"
                              :disabled="isDisabled"
                            >
                            </v-switch>
                          </vonap-form-label>
                        </v-flex>
                      </v-layout>

                      <vonap-form-label label="Markets">
                        <vonap-switch-list
                          hide-search
                          :is-disabled="isDisabled"
                          :show-select-all="false"
                          :use-v-card="false"
                          flex-size="xs12"
                          :data-source="marketsDataSourceInOrder"
                          data-value-field="marketId"
                          data-text-field="marketCode"
                          label="Markets"
                          v-model="currentClient.marketPrefixDataSource"
                          width="100px"
                          v-slot="{item, changeSelection}"
                        >
                          <span>
                            {{ item.marketCode }}
                          </span>

                          <v-switch
                            readonly
                            v-model="item.selected"
                            class="mt-0 configSwitch"
                            height="20px"
                            :disabled="isDisabled"
                          >
                            <template slot="label">
                              <span v-if="!item.selected">
                                {{ item.marketCode }}
                              </span>
                              <v-text-field
                                v-else
                                v-model="item.originalItem.prefix"
                                title="Market prefix"
                                @click.stop="() => {}"
                              />
                            </template>
                          </v-switch>
                        </vonap-switch-list>
                      </vonap-form-label>
                      <vonap-form-label label="Logo">
                        <input type="file" accept="image/*" ref="logo" />
                        <v-img
                          :src="`/api/config/Client/getLogo?clientId=${currentClient.clientId}`"
                        />
                      </vonap-form-label>
                    </v-flex>

                    <v-flex xs12 md7 lg8>
                      <vonap-form-label label="Currency">
                        <vonap-switch-list
                          max-height="400px"
                          :is-disabled="isDisabled"
                          :use-v-card="false"
                          flex-size="xs3 sm2 md6 lg4"
                          :data-source="masterClient.fxCcies"
                          data-value-field="ccy"
                          data-text-field="ccy"
                          label="Ccies"
                          v-model="currentClient.ccies"
                          width="100px"
                        />
                      </vonap-form-label>

                      <vonap-form-label label="Functional Ccy" style="margin-top: 16px">
                        <v-layout row wrap>
                          <v-flex xs6 md12 lg6>
                            <kendo-dropdownlist
                              style="width: 82px"
                              :data-source="currentClient.ccies"
                              :data-text-field="'ccy'"
                              :data-value-field="'ccy'"
                              :filter="'contains'"
                              :options-label="'Functional Ccy'"
                              required
                              :rules="[v => !!v || 'Functional ccy is required']"
                              v-model="currentClient.functionalCcy"
                              :disabled="isDisabled"
                            ></kendo-dropdownlist>
                          </v-flex>
                        </v-layout>
                      </vonap-form-label>

                      <vonap-form-label label="Copy config from client" style="margin-top: 16px">
                        <v-layout row wrap>
                          <v-flex xs6 md12 lg6 style="padding-top: 8px">
                            <kendo-dropdownlist
                              style="width: 100%"
                              :data-source="
                                this.clientId == -1
                                  ? masterClient.allClients
                                  : currentClient.allClients
                              "
                              :data-text-field="'name'"
                              :data-value-field="'clientId'"
                              :filter="'contains'"
                              :options-label="'From'"
                              v-model="currentClient.configClientId"
                              @change="onConfigClientChange"
                              :disabled="isDisabled"
                            ></kendo-dropdownlist>
                          </v-flex>
                          <v-flex xs6 md12 lg6>
                            <v-btn
                              v-if="canResetConfig"
                              @click="resetConfiguration"
                              :disabled="isDisabled"
                              dark
                              small
                              color="primary"
                              style="width: 100%"
                            >
                              <v-icon>miscellaneous_services</v-icon>
                              Create/Reset Configuration
                            </v-btn>
                          </v-flex>
                        </v-layout>
                      </vonap-form-label>

                      <vonap-form-label
                        label="Undelivered Balance Message (ALL)"
                        style="margin-top: 16px"
                      >
                        <v-layout row wrap>
                          <v-flex xs6 md12 lg6 style="padding-top: 8px">
                            <kendo-dropdownlist
                              style="width: 100%"
                              :data-source="sendingUndeliveredBalanceMessageData"
                              :data-text-field="'sendingMessageName'"
                              :data-value-field="'sendingMessageId'"
                              v-model="currentClient.sendingUndeliveredBalanceMessageId"
                              :disabled="isDisabled"
                            ></kendo-dropdownlist>
                          </v-flex>
                        </v-layout>
                      </vonap-form-label>
                      <vonap-form-label
                        label="Undelivered Balance Message (Forward)"
                        style="margin-top: 16px"
                      >
                        <v-layout row wrap>
                          <v-flex xs6 md12 lg6 style="padding-top: 8px">
                            <kendo-dropdownlist
                              style="width: 100%"
                              :data-source="sendingUndeliveredBalanceMessageData"
                              :data-text-field="'sendingMessageName'"
                              :data-value-field="'sendingMessageId'"
                              v-model="currentClient.forwardSendingUndeliveredBalanceMessageId"
                              :disabled="isDisabled"
                            ></kendo-dropdownlist>
                          </v-flex>
                        </v-layout>
                      </vonap-form-label>
                      <vonap-form-label
                        label="Undelivered Balance Message (Option)"
                        style="margin-top: 16px"
                      >
                        <v-layout row wrap>
                          <v-flex xs6 md12 lg6 style="padding-top: 8px">
                            <kendo-dropdownlist
                              style="width: 100%"
                              :data-source="sendingUndeliveredBalanceMessageData"
                              :data-text-field="'sendingMessageName'"
                              :data-value-field="'sendingMessageId'"
                              v-model="currentClient.optionSendingUndeliveredBalanceMessageId"
                              :disabled="isDisabled"
                            ></kendo-dropdownlist>
                          </v-flex>
                        </v-layout>
                      </vonap-form-label>
                    </v-flex>
                    <v-flex v-if="!this.isNew" xs12>
                      <kendo-grid
                        style="height: 234px; width: 614px; margin-top: 16px"
                        :dataSource="hedgeStrategyDataSource"
                        :editable="{mode: 'inline'}"
                        :toolbar="'<h3>Hedge Strategy</h3>'"
                        ref="hedgeStrategyGrid"
                        id="hedgeStrategyGrid"
                        :filterable="true"
                        :sortable="true"
                        :columns="hedgeStrategyColumns"
                      >
                      </kendo-grid>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-container>
          </v-tab-item>

          <v-tab-item v-for="market in marketsInOrder" :key="market.marketId">
            <v-container fluid py-0 my-0>
              <v-layout row>
                <v-flex xs1> </v-flex>
              </v-layout>
            </v-container>
            <market-router
              :market="market"
              :data-source="masterClient"
              :current-client="currentClient"
              :is-disabled="isDisabled"
            />
          </v-tab-item>
        </v-tabs>
      </v-form>
    </vonap-fullscreen-dialog>
  </vonap-promised>
</template>

<script>
import axios from 'axios'
import {mapGetters} from 'vuex'
import VonapSwitchList from '@/components/VonapSwitchList'
import VonapFullscreenDialog from '@/components/VonapFullscreenDialog'
import VonapFormLabel from '@/components/VonapFormLabel'
import VonapPromised from '@/components/VonapPromised'
import {readOnlyEditor, jsonDropDownEditor, numericEditor} from '@/helpers/kendo-editors'
import notification from '@/helpers/notification'
import MarketRouter from './MarketRouter'
import security from '@/mixins/security'
const $ = kendo.jQuery

export default {
  name: 'ClientForm',
  props: {
    value: Boolean,
    clientId: Number,
  },
  mixins: [security],
  data() {
    return {
      isNew: this.clientId == -1,
      saving: false,
      errored: false,
      valid: false,
      currentClient: {
        name: '',
        clientId: -1,
        configClientId: 0,
        fiscalYearEnd: 12,
        timeZoneId: 1,
        historicalMonthsNb: 12,
        ccies: [],
        ccyPairs: [],
        products: [],
        counterparties: [],
        processes: [],
        marketPrefixDataSource: [],
        allClients: [],
      },
      usersClientData: [],
      masterClient: {
        ccies: [],
        fxCcies: [],
        ccyPairs: [],
        counterparties: [],
        marketPrefixDataSource: [],
        allClients: [],
      },
      timezones: [
        {value: 1, name: 'America/New_York'},
        {value: 2, name: 'America/Chicago'},
        {value: 3, name: 'America/Denver'},
        {value: 4, name: 'America/Los_Angeles'},
      ],
      months: [
        {month: 1, name: 'January'},
        {month: 2, name: 'February'},
        {month: 3, name: 'March'},
        {month: 4, name: 'April'},
        {month: 5, name: 'May'},
        {month: 6, name: 'June'},
        {month: 7, name: 'July'},
        {month: 8, name: 'August'},
        {month: 9, name: 'September'},
        {month: 10, name: 'October'},
        {month: 11, name: 'November'},
        {month: 12, name: 'December'},
      ],
      selectedTab: 0,
      sendingUndeliveredBalanceMessageData: [],
      hedgeStrategyDataSource: new kendo.data.DataSource({
        type: 'aspnetmvc-ajax',
        serverFiltering: true,
        serverSorting: true,
        pageSize: 10,
        transport: {
          read: {
            type: 'GET',
            url: '/api/config/Client/getFlowTypeHedgeStrategy',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
          update: {
            type: 'PUT',
            url: '/api/config/Client/updateFlowTypeHedgeStrategy',
            dataType: 'json',
          },
        },
        requestEnd: e => {
          if (e.type == 'update') {
            this.$refs.hedgeStrategyGrid.kendoWidget().dataSource.read()
          }
        },
        schema: {
          errors: 'errors',
          total: 'total',
          data: 'data',
          model: {
            id: 'flowTypeId',
            fields: {
              flowTypeId: {type: 'number', validation: {required: true}},
              flowTypeName: {type: 'string', validation: {required: true}},
              hedgeStrategyId: {type: 'number', validation: {required: true}},
              hedgeStrategyName: {type: 'string', validation: {required: true}},
              hedgeStrategyMonth: {type: 'number', validation: {required: false}},
              hedgeStrategyMonthName: {type: 'string', validation: {required: true}},
              hedgeStrategyHorizon: {type: 'number', validation: {required: false}},
              rollingLookBack: {type: 'number', validation: {required: false}},
            },
          },
        },
      }),
    }
  },
  methods: {
    async formSubmit() {
      if (!this.$refs.form.validate()) return

      const ccyPairs = this.currentClient.ccyPairs
      const markets = this.marketsInOrder
      for (let i = 0; i < markets.length; i++) {
        let marketId = +markets[i].marketId
        let marketCode = markets[i].marketCode
        let containsCcyPair = ccyPairs.filter(e => e.marketId == marketId).length > 0
        if (!containsCcyPair) {
          notification(
            `Unable to save , please select at least one Ccy/Comm on tab '${marketCode}'.`,
            'error',
          )
          return
        }
      }

      this.currentClient.clientId = +this.currentClient.clientId
      this.currentClient.configClientId = +this.currentClient.configClientId
      this.currentClient.fiscalYearEnd = +this.currentClient.fiscalYearEnd
      this.currentClient.timeZoneId = +this.currentClient.timeZoneId
      this.currentClient.historicalMonthsNb = +this.currentClient.historicalMonthsNb

      this.saving = true
      if (this.currentClient.clientId != -1) {
        await axios.put('/api/config/Client', this.currentClient).catch(() => (this.errored = true))
        this.saving = false
      } else {
        const {data} = await axios
          .post('/api/config/Client', this.currentClient)
          .catch(() => (this.errored = true))
        const newClient = data.data[0]
        this.isNew = false
        this.currentClient.clientId = newClient.clientId
        this.saving = false
      }
      this.$store.dispatch('authentication/getNewAccessToken')

      const [logo] = this.$refs.logo.files
      const data = logo ? new FormData() : null
      if (data) {
        data.append('file', logo, 'logo.png')
        const response = await axios({
          url: '/api/config/Client/uploadLogo',
          method: 'post',
          params: {clientId: this.currentClient.clientId},
          data,
          headers: {'Content-Type': 'multipart/form-data'},
        })

        if (response) {
          this.$refs.logo.value = ''
        }
      }
      const selectedClientId = +this.selectedClientId

      if (this.currentClient.clientId == selectedClientId) {
        this.$store.dispatch('authentication/setSelectedClientId', {selectedClientId})
      }
      this.$emit('save')
    },
    resetConfiguration() {
      if (this.currentClient.configClientId == -1) {
        notification('Select a valid client to copy the configuration.', 'error')
        return
      }
      this.saving = true
      axios
        .post('/api/config/Client/CopyClientConfig', {
          clientId: +this.currentClient.clientId,
          fromClientId: +this.currentClient.configClientId,
          functionalCcy: this.currentClient.functionalCcy,
        })
        .then(response => {
          if (response.status == 200) {
            let responseType = response.headers['content-type']
            if (responseType.includes('application/json')) {
              let parseResponse = response.data
              if (parseResponse.status == 'error') {
                notification(parseResponse.message, 'error')
              } else {
                {
                  notification(parseResponse.message)
                }
              }
            }
          }
        })
        .catch(() => (this.errored = true))
      this.saving = false
    },
    async getData() {
      if (this.clientId != -1) {
        const {data: currentClient} = await axios.get('/api/config/Client/getClientData', {
          params: {clientId: this.clientId},
        })

        this.currentClient = currentClient
      }

      const {data: usersClientData} = await axios.get('/api/config/Client/getUsersByClient', {
        params: {clientId: this.clientId},
      })
      this.usersClientData = usersClientData

      const {data: sendingUndeliveredBalanceMessageData} = await axios.get(
        '/api/config/Client/getSendingUndeliveredBalanceMessageData',
        {},
      )
      this.sendingUndeliveredBalanceMessageData = sendingUndeliveredBalanceMessageData

      if (this.clientId != 0) {
        const {data: masterClient} = await axios.get('/api/config/Client/getClientData', {
          params: {clientId: 0},
        })

        this.masterClient = {
          ccies: masterClient.ccies,
          fxCcies: masterClient.fxCcies,
          ccyPairs: masterClient.ccyPairs,
          products: masterClient.products,
          counterparties: masterClient.counterparties,
          processes: masterClient.processes,
          marketPrefixDataSource: masterClient.marketPrefixDataSource,
          allClients: masterClient.allClients,
        }
      } else {
        const getData = action =>
          axios
            .get(`/api/config/Client/${action}`, {params: {clientId: -1}})
            .then(response => response.data)
        const promises = [
          getData('getAllAvailableCcies'),
          getData('getAllAvailableFxCcies'),
          getData('getAllAvailableCcyPairs'),
          getData('getAllAvailableProducts'),
          getData('getAllAvailableCounterparties'),
          getData('getAllAvailableProcesses'),
          getData('getAllAvailableMarkets'),
        ]

        const [
          ccies,
          fxCcies,
          ccyPairs,
          products,
          counterparties,
          processes,
          marketPrefixDataSource,
          allClients,
        ] = await Promise.all(promises)

        this.masterClient = {
          ccies,
          fxCcies,
          ccyPairs,
          products,
          counterparties,
          processes,
          marketPrefixDataSource,
          allClients,
        }
      }
    },
    async onConfigClientChange({sender}) {
      const clientId = sender.value()
      const {data: configClient} = await axios.get('/api/config/Client/getClientData', {
        params: {clientId},
      })

      if (configClient) {
        this.currentClient.forwardSendingUndeliveredBalanceMessageId =
          configClient.forwardSendingUndeliveredBalanceMessageId ||
          this.currentClient.forwardSendingUndeliveredBalanceMessageId
        this.currentClient.optionSendingUndeliveredBalanceMessageId =
          configClient.optionSendingUndeliveredBalanceMessageId ||
          this.currentClient.optionSendingUndeliveredBalanceMessageId
        this.currentClient.sendingUndeliveredBalanceMessageId =
          configClient.sendingUndeliveredBalanceMessageId ||
          this.currentClient.sendingUndeliveredBalanceMessageId
      }
    },
  },
  computed: {
    canResetConfig() {
      const reset = this.currentRole == 'SuperAdmin' && !this.isNew && !this.isReadOnly
      return reset
    },
    isProfilePage() {
      return this.$route.path == '/client/profile'
    },
    canSetActive() {
      const canSet = this.currentRole == 'SuperAdmin' && !this.isNew && !this.isReadOnly
      return canSet
    },
    isDisabled() {
      const enabled = this.isProfilePage
        ? this.currentRole == 'SuperAdmin' || this.clientId != 0
        : !this.isReadOnly
      return !enabled
    },
    marketsDataSourceInOrder() {
      const reduceBy = (prop, array) =>
        array.reduce((acc, item) => {
          acc[item[prop]] = item
          return acc
        }, {})

      const source = reduceBy('marketId', this.masterClient.marketPrefixDataSource)
      const selected = reduceBy('marketId', this.currentClient.marketPrefixDataSource)
      const combined = {...source, ...selected}

      const array = Object.keys(combined).map(key => combined[key])
      return array.sort((a, b) => (a.marketId > b.marketId ? 1 : -1))
    },
    marketsInOrder() {
      return this.currentClient.marketPrefixDataSource
        .filter(market => market.marketId > 0)
        .slice()
        .sort((a, b) => (a.marketId > b.marketId ? 1 : -1))
    },
    show: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    promise() {
      return this.getData()
    },
    hedgeStrategyColumns() {
      return [
        {
          editable: true,
          command: [{name: 'edit'}],
          width: 60,
        },
        {
          field: 'flowTypeName',
          title: 'Flow Type',
          width: 142,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align:left'},
          editor: function(container, options) {
            return readOnlyEditor.call(this, container, options)
          },
        },
        {
          field: 'hedgeStrategyName',
          title: 'Type',
          width: 95,
          attributes: {style: 'text-align: left;'},
          editor: function(container, options) {
            return jsonDropDownEditor(
              'hedgeStrategyId',
              'hedgeStrategyName',
              '/api/config/Client/getHedgeStrategy',
              {
                dataBound: function() {
                  this.trigger('change')
                },
                change: function(e) {
                  const isPrice = e.sender.value() == 1

                  $('#hedgeStrategyGrid')
                    .data('kendoGrid')
                    .tbody.find("[data-role='dropdownlist'], [data-role='numerictextbox']")
                    .each(function() {
                      var dropdownlist = $(this).data('kendoDropDownList')
                      var numericTextBox = $(this).data('kendoNumericTextBox')

                      if (dropdownlist && dropdownlist.element[0].id == 'hedgeStrategyMonthName') {
                        if (isPrice) {
                          dropdownlist.wrapper.hide()
                        } else {
                          dropdownlist.wrapper.show()
                        }
                      }
                      if (dropdownlist && dropdownlist.element[0].id == 'hedgeStrategyHorizon') {
                        if (isPrice) {
                          dropdownlist.wrapper.hide()
                        } else {
                          dropdownlist.wrapper.show()
                        }
                      }
                      if (numericTextBox && numericTextBox.element[0].id == 'rollingLookBack') {
                        if (!isPrice) {
                          numericTextBox.wrapper.hide()
                        } else {
                          numericTextBox.wrapper.show()
                        }
                      }
                    })
                },
              },
            ).call(this, container, options)
          },
        },
        {
          field: 'hedgeStrategyMonthName',
          title: 'Start Month',
          width: 100,
          attributes: {style: 'text-align: left;'},
          editor: function(container, options) {
            return jsonDropDownEditor(
              'hedgeStrategyMonth',
              'hedgeStrategyMonthName',
              '/api/config/Client/getMonths',
            ).call(this, container, options)
          },
        },
        {
          field: 'hedgeStrategyHorizon',
          title: 'Horizon',
          width: 90,
          attributes: {style: 'text-align: left;'},
          editor: function(container, options) {
            return jsonDropDownEditor(
              'hedgeStrategyHorizon',
              'hedgeStrategyHorizon',
              '/api/config/Client/getHorizonMonths',
            ).call(this, container, options)
          },
        },
        {
          field: 'rollingLookBack',
          title: 'Rolling Lookback',
          width: 100,
          attributes: {style: 'text-align: left;'},
          editor: function(container, options) {
            return numericEditor(
              {
                min: 1,
                max: 12,
                decimals: 0,
                format: '{0:n0}',
                spinners: false,
              },
              true,
            ).call(this, container, options)
          },
        },
      ].filter(c => c.editable !== false)
    },
    ...mapGetters({selectedClientId: 'authentication/selectedClientId'}),
    ...mapGetters({currentRole: 'authentication/currentRole'}),
  },
  components: {
    VonapFullscreenDialog,
    VonapFormLabel,
    VonapPromised,
    VonapSwitchList,
    MarketRouter,
  },
}
</script>
